import type {
  LeadFormFieldDto,
  ProfileModuleDto,
} from '@up2stay/up2promo-api-client';
import type { GoogleMapsLocationSource } from '@up2stay/up2promo-commons/profile';

import { PREVIEW_SAMPLE_AVATAR_PATH } from '@/core/lib/file-url';
import { buildPreviewModules } from '@/core/lib/profile-preview';
import type { TemplateFormData } from '@/core/lib/profile-template-form';
import {
  buildProfileStylingPayload,
  PROFILE_STYLING_FORM_DEFAULTS,
} from '@/core/lib/profile-styling-form';

type WorkspacePhotoSlice = {
  photo?: { path?: string | null } | null;
} | null;

export type TemplateDetailPreviewProfileData = {
  id: string;
  title: string;
  jobTitle: string | undefined;
  company: string | undefined;
  location: string | null | undefined;
  profileImage: { path: string } | null;
  showProfileImage: boolean;
  showUp2promoBranding: boolean;
  styling: ReturnType<typeof buildProfileStylingPayload>;
  ownerPhoto: null;
  workspacePhoto: { path: string } | null;
  modules: ProfileModuleDto[];
  leadForm: {
    enabled: boolean;
    visibilityMode: 'off' | 'manual' | 'auto_open';
    fields: LeadFormFieldDto[];
  };
};

export function buildTemplateDetailPreviewProfileData(input: {
  previewSampleName: string;
  form: TemplateFormData;
  modules: ProfileModuleDto[];
  fixedLeadFormFields: LeadFormFieldDto[];
  workspace: WorkspacePhotoSlice;
  locationsById?:
    | ReadonlyMap<string, GoogleMapsLocationSource>
    | Record<string, GoogleMapsLocationSource>;
}): TemplateDetailPreviewProfileData {
  const previewShowProfileImage = input.form.showProfileImage ?? true;
  const previewShowUp2promoBranding = input.form.showUp2promoBranding ?? true;

  return {
    id: 'template-preview',
    title: input.previewSampleName,
    jobTitle: input.form.jobTitle || undefined,
    company: input.form.company || undefined,
    location: input.form.location || null,
    profileImage: previewShowProfileImage
      ? { path: PREVIEW_SAMPLE_AVATAR_PATH }
      : null,
    showProfileImage: previewShowProfileImage,
    showUp2promoBranding: previewShowUp2promoBranding,
    styling: buildProfileStylingPayload(
      {
        layout: input.form.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
        theme: input.form.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
        headerStyle:
          input.form.headerStyle ?? PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
        headerImage: input.form.headerImage ?? null,
        buttonShape: input.form.buttonShape ?? null,
        accentColor: input.form.accentColor ?? null,
      },
      { keepEphemeralPaths: true },
    ),
    ownerPhoto: null,
    workspacePhoto: input.workspace?.photo?.path
      ? { path: input.workspace.photo.path }
      : null,
    modules:
      buildPreviewModules({
        modules: input.modules,
        locationsById: input.locationsById,
      }) ?? [],
    leadForm: {
      enabled: input.form.leadFormEnabled ?? false,
      visibilityMode: input.form.leadFormVisibilityMode ?? 'off',
      fields: input.fixedLeadFormFields,
    },
  };
}
